import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophoneSlash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import Avatar from '../Avatar';
import { orgLabel } from '../../utils/orgTaxonomy';

/**
 * One person on the call.
 *
 * Shows their camera when there is one and their avatar when there is not, plus
 * the two things a grid has to answer that a single-person call screen never
 * had to: which of these people is still connecting, and which of them is
 * muted.
 *
 * Per-person status is the substantive change from the old layout. A call with
 * one other person could say "Connecting…" once for the whole call, because
 * there was only one person to wait for. In a group each one comes up at a
 * different moment, and one can drop while the rest are fine — so
 * the status belongs on the tile rather than under the title.
 */

const statusFor = (participant) => {
    if (participant.state === 'ringing') return 'Ringing…';
    if (participant.isSelf) return '';
    switch (participant.connectionState) {
        case 'failed': return participant.failure || 'Could not connect';
        case 'disconnected': return 'Reconnecting…';
        case 'connected': return '';
        default: return 'Connecting…';
    }
};

const CallTile = ({ participant, stream, speaking, mirror = false }) => {
    const videoRef = useRef(null);
    const person = participant.person || {};
    const status = statusFor(participant);
    const hasVideo = Boolean(stream) && participant.camOn !== false;

    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        el.srcObject = stream || null;
        if (stream) el.play().catch(() => { /* autoplay is muted, so this is rare */ });
    }, [stream]);

    const classes = [
        'call-tile',
        participant.isSelf && 'is-self',
        speaking && 'is-speaking',
        participant.connectionState === 'failed' && 'is-failed',
        !hasVideo && 'is-audio'
    ].filter(Boolean).join(' ');

    return (
        <div className={classes}>
            {hasVideo ? (
                /*
                 * `muted` is not optional on ANY tile here, including the remote
                 * ones. Audio is played by the sinks in the provider, which
                 * survive the window being minimised; letting a tile play it too
                 * would double every voice. On the self tile it is worse than
                 * that — an unmuted self-view plays your own microphone back at
                 * about a tenth of a second, which is textbook echo and reads to
                 * the user as a broken network rather than a missing attribute.
                 */
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={mirror ? 'call-tile-video is-mirrored' : 'call-tile-video'}
                />
            ) : (
                <div className="call-tile-avatar">
                    <Avatar
                        name={person.name}
                        profilePic={person.profilePic}
                        className="call-tile-photo"
                    />
                </div>
            )}

            {status && (
                <div className="call-tile-status" role="status">
                    {participant.connectionState === 'failed' && (
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                    )}
                    {status}
                </div>
            )}

            <div className="call-tile-bar">
                {participant.micOn === false && (
                    <FontAwesomeIcon icon={faMicrophoneSlash} className="call-tile-muted" />
                )}
                <span className="call-tile-name">
                    {participant.isSelf
                        ? (participant.deviceLabel ? `You (${participant.deviceLabel})` : 'You')
                        : (person.name || 'Someone')}
                </span>
                {participant.isInitiator && <span className="call-tile-host-label">Host</span>}
                {!participant.isSelf && orgLabel(person.jobTitle) && (
                    <span className="call-tile-sub">{orgLabel(person.jobTitle)}</span>
                )}
            </div>
        </div>
    );
};

export default CallTile;
